import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  FileText, Plus, Trash2, Save, ChevronLeft, ChevronRight, 
  Type, PenTool, Calendar, CheckSquare, User, Mail, Loader2,
  GripVertical, X, Upload, Users, ListOrdered, Copy, CheckCircle,
  Grid3x3, FilePlus, ArrowUp, ArrowDown, FileUp, Replace, Stamp,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import DropZone from './DropZone';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenTool, color: 'bg-blue-500' },
  { type: 'text', label: 'Text', icon: Type, color: 'bg-green-500' },
  { type: 'date', label: 'Date', icon: Calendar, color: 'bg-purple-500' },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, color: 'bg-orange-500' },
  { type: 'checkbox_group', label: 'Checkbox Group', icon: ListOrdered, color: 'bg-red-500' },
  { type: 'initials', label: 'Initials', icon: PenTool, color: 'bg-pink-500' },
  { type: 'name', label: 'Name', icon: User, color: 'bg-cyan-500' },
  { type: 'email', label: 'Email', icon: Mail, color: 'bg-yellow-500' },
];

const DEFAULT_FIELD_SIZE = {
  signature: { width: 20, height: 8 },
  text: { width: 25, height: 4 },
  date: { width: 15, height: 4 },
  checkbox: { width: 3, height: 3 },
  checkbox_group: { width: 3, height: 3 },
  initials: { width: 10, height: 6 },
  name: { width: 25, height: 4 },
  email: { width: 30, height: 4 },
};

const CHECKBOX_GROUP_VALIDATIONS = [
  { value: 'select_one', label: 'Exactly one (radio behavior)' },
  { value: 'select_at_least', label: 'At least N of them' },
  { value: 'select_all', label: 'All of them' },
];

const SIGNER_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-yellow-500',
  'bg-red-500',
];

export default function ESignatureTemplateEditor({ templateId, onBack, onSave }) {
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [signerRoles, setSignerRoles] = useState(['Signer']);
  const [signingOrderType, setSigningOrderType] = useState('parallel');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]); // For multi-select
  const [activeSignerIndex, setActiveSignerIndexRaw] = useState(0); // Currently selected signer
  const [dragRoleIndex, setDragRoleIndex] = useState(null); // Drag-to-reorder signer roles

  // Set the active signer AND remember it for this template, so the choice
  // sticks across new fields and across reopening the editor.
  const setActiveSignerIndex = useCallback((idx) => {
    setActiveSignerIndexRaw(idx);
    try {
      if (templateId != null) localStorage.setItem(`sig_active_signer_${templateId}`, String(idx));
    } catch (e) { /* ignore */ }
  }, [templateId]);
  const [showBulkCheckboxModal, setShowBulkCheckboxModal] = useState(false);
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkColumns, setBulkColumns] = useState(2);
  const [showPageMgmtModal, setShowPageMgmtModal] = useState(false);
  const [pageOpInProgress, setPageOpInProgress] = useState(false);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [checkboxGroups, setCheckboxGroups] = useState({}); // Track checkbox groups
  const [showMultiSelectMode, setShowMultiSelectMode] = useState(false);
  const [stampMode, setStampMode] = useState(false);     // "Place many" tool toggle
  const [stampType, setStampType] = useState(null);      // Active field type to stamp
  const [bulkType, setBulkType] = useState('checkbox');  // Field type for bulk-add modal
  // Mobile: which bottom sheet is open — 'tools' | 'properties' | null
  const [mobileSheet, setMobileSheet] = useState(null);
  const [renderWidth, setRenderWidth] = useState(612);
  
  const pdfContainerRef = useRef(null);
  const pageRef = useRef(null);
  const blobUrlRef = useRef(null); // Track blob URL for cleanup
  // Remembers the last-used size per field type so Stamp mode (and single add)
  // reuse the size you last resized a field of that type to.
  const lastSizeByTypeRef = useRef({});
  // Active resize gesture state (set on a resize-handle pointerdown).
  const resizeStateRef = useRef(null);

  // Function to load PDF separately (for retry)
  const loadPdf = useCallback(async () => {
    if (!templateId) return;
    
    setPdfLoading(true);
    setPdfError(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch PDF as blob with retry logic
      let retries = 3;
      let pdfBlob = null;
      
      while (retries > 0 && !pdfBlob) {
        try {
          const pdfResponse = await axios.get(
            `${API}/esignature/templates/${templateId}/pdf`,
            { 
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
              timeout: 30000
            }
          );
          pdfBlob = pdfResponse.data;
        } catch (pdfError) {
          console.warn(`PDF fetch attempt failed, ${retries - 1} retries left:`, pdfError);
          retries--;
          if (retries > 0) {
            await new Promise(r => setTimeout(r, 1000));
          }
        }
      }
      
      if (pdfBlob) {
        // Clean up old blob URL if exists
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        
        const blob = new Blob([pdfBlob], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setPdfUrl(blobUrl);
        setPdfError(false);
      } else {
        setPdfError(true);
        toast.error('Failed to load PDF after multiple attempts');
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
      setPdfError(true);
    } finally {
      setPdfLoading(false);
    }
  }, [templateId]);

  // Fetch template data
  useEffect(() => {
    let isMounted = true;
    
    const fetchTemplate = async () => {
      if (!templateId) {
        setLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/esignature/templates/${templateId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!isMounted) return;
        
        setTemplate(response.data);
        setFields(response.data.fields || []);
        const roles = response.data.signer_roles || ['Signer'];
        setSignerRoles(roles);
        // Restore the last active signer the user was plotting for on this
        // template, so reopening keeps "their" signer until they pick another.
        try {
          const saved = parseInt(localStorage.getItem(`sig_active_signer_${templateId}`), 10);
          if (!isNaN(saved) && saved >= 0 && saved < roles.length) {
            setActiveSignerIndex(saved);
          }
        } catch (e) { /* ignore */ }
        setSigningOrderType(response.data.signing_order_type || 'parallel');
        setNumPages(response.data.page_count || 1);
        
        // Build checkbox groups from existing fields
        const groups = {};
        (response.data.fields || []).forEach(field => {
          if (field.group_id) {
            if (!groups[field.group_id]) {
              groups[field.group_id] = {
                validation: field.group_validation || 'optional',
                fields: []
              };
            }
            groups[field.group_id].fields.push(field.id);
          }
        });
        setCheckboxGroups(groups);
        
        // Load PDF
        await loadPdf();
        
      } catch (error) {
        console.error('Error fetching template:', error);
        if (isMounted) {
          toast.error('Failed to load template');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchTemplate();
    
    return () => {
      isMounted = false;
      // Clean up blob URL on unmount
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [templateId, loadPdf]);

  // Responsive PDF width: full-width on phones, fixed 612px on md+ screens so
  // the desktop editor is untouched. Overlays are %-based, so they scale too.
  useEffect(() => {
    const measure = () => {
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      if (isDesktop) { setRenderWidth(612); return; }
      const el = pdfContainerRef.current;
      const avail = (el?.clientWidth || window.innerWidth) - 32;
      setRenderWidth(Math.max(280, Math.min(612, avail)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [pdfUrl, loading]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    const { width, height } = page;
    setPageSize({ width, height });
  };

  const getSignerColor = (index) => {
    return SIGNER_COLORS[index % SIGNER_COLORS.length];
  };

  // Add a new field - assigns to active signer
  const addField = useCallback((type) => {
    const isCheckboxGroup = type === 'checkbox_group';
    const memType = isCheckboxGroup ? 'checkbox' : type;
    const defaultSize = lastSizeByTypeRef.current[memType] || DEFAULT_FIELD_SIZE[type];
    
    const newField = {
      id: `field_${Date.now()}`,
      type: isCheckboxGroup ? 'checkbox' : type,
      label: `${type.charAt(0).toUpperCase() + type.slice(1)} ${fields.length + 1}`,
      page: currentPage,
      x: 10 + (fields.filter(f => f.page === currentPage).length * 2) % 60,
      y: 10 + (fields.filter(f => f.page === currentPage).length * 5) % 60,
      width: defaultSize.width,
      height: defaultSize.height,
      required: true,
      signer_index: activeSignerIndex,
      placeholder: '',
      default_value: '',
      // Checkbox group properties
      group_id: isCheckboxGroup ? `group_${Date.now()}` : null,
      group_validation: isCheckboxGroup ? 'select_one' : null,
      checkbox_label: isCheckboxGroup ? 'Option 1' : null,
    };
    
    // If creating checkbox group, also create initial group entry
    if (isCheckboxGroup) {
      setCheckboxGroups(prev => ({
        ...prev,
        [newField.group_id]: {
          validation: 'select_one',
          fields: [newField.id]
        }
      }));
    }
    
    setFields([...fields, newField]);
    setSelectedField(newField.id);
    setSelectedFields([newField.id]);
  }, [fields, currentPage, activeSignerIndex]);

  // Add a field at a specific position (in %) — used by Stamp mode so each
  // click on the document drops a new field of the active type.
  const addFieldAt = useCallback((type, cx, cy) => {
    const isCheckboxGroup = type === 'checkbox_group';
    const memType = isCheckboxGroup ? 'checkbox' : type;
    const size = lastSizeByTypeRef.current[memType] || DEFAULT_FIELD_SIZE[type] || DEFAULT_FIELD_SIZE.text;
    const uid = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const gid = `group_${uid}`;
    const newField = {
      id: `field_${uid}`,
      type: isCheckboxGroup ? 'checkbox' : type,
      label: `${type.charAt(0).toUpperCase() + type.slice(1)} ${fields.length + 1}`,
      page: currentPage,
      // Center the field on the cursor, clamped inside the page.
      x: Math.max(0, Math.min(100 - size.width, cx - size.width / 2)),
      y: Math.max(0, Math.min(100 - size.height, cy - size.height / 2)),
      width: size.width,
      height: size.height,
      required: true,
      signer_index: activeSignerIndex,
      placeholder: '',
      default_value: type === 'checkbox' ? false : '',
      group_id: isCheckboxGroup ? gid : null,
      group_validation: isCheckboxGroup ? 'select_one' : null,
      checkbox_label: isCheckboxGroup ? 'Option 1' : null,
    };
    if (isCheckboxGroup) {
      setCheckboxGroups(prev => ({ ...prev, [gid]: { validation: 'select_one', fields: [newField.id] } }));
    }
    setFields(prev => [...prev, newField]);
    setSelectedField(newField.id);
    setSelectedFields([newField.id]);
  }, [fields, currentPage, activeSignerIndex]);

  // Bulk-create N fields of ANY type in a tidy grid. Each gets its own field
  // so admins can drag them around individually afterwards.
  const addFieldGrid = useCallback((type, count, columns) => {
    const n = Math.min(Math.max(parseInt(count, 10) || 0, 1), 200);
    const cols = Math.min(Math.max(parseInt(columns, 10) || 1, 1), 10);
    const size = DEFAULT_FIELD_SIZE[type] || DEFAULT_FIELD_SIZE.text;
    const startX = 6;
    const startY = 8;
    const xStep = Math.max(size.width + 2, Math.min(90 / cols, 45));
    const yStep = size.height + 3;
    const ts = Date.now();
    const existingOnPage = fields.filter(f => f.page === currentPage).length;
    const newFields = [];
    for (let i = 0; i < n; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      newFields.push({
        id: `field_${ts}_${i}`,
        type,
        label: `${type.charAt(0).toUpperCase() + type.slice(1)} ${existingOnPage + i + 1}`,
        page: currentPage,
        x: Math.min(startX + col * xStep, Math.max(0, 100 - size.width)),
        y: Math.min(startY + row * yStep, Math.max(0, 100 - size.height)),
        width: size.width,
        height: size.height,
        required: false,
        signer_index: activeSignerIndex,
        placeholder: '',
        default_value: type === 'checkbox' ? false : '',
        group_id: null,
        group_validation: null,
        checkbox_label: null,
        order: existingOnPage + i,
      });
    }
    setFields([...fields, ...newFields]);
    if (newFields.length > 0) {
      setSelectedField(newFields[0].id);
      setSelectedFields(newFields.map(f => f.id));
    }
    const labelType = (FIELD_TYPES.find(t => t.type === type)?.label || type).toLowerCase();
    toast.success(`Added ${n} ${labelType}${n !== 1 ? 's' : ''}`);
  }, [fields, currentPage, activeSignerIndex]);

  // Reorder a single field's `order` within its page. Used by the drag-reorder
  // list in the Field Properties panel.
  const reorderField = useCallback((fieldId, direction) => {
    setFields(prev => {
      const target = prev.find(f => f.id === fieldId);
      if (!target) return prev;
      const samePage = prev.filter(f => f.page === target.page);
      samePage.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = samePage.findIndex(f => f.id === fieldId);
      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= samePage.length) return prev;
      const a = samePage[idx];
      const b = samePage[swapWith];
      const aOrder = a.order ?? idx;
      const bOrder = b.order ?? swapWith;
      return prev.map(f => {
        if (f.id === a.id) return { ...f, order: bOrder };
        if (f.id === b.id) return { ...f, order: aOrder };
        return f;
      });
    });
  }, []);

  // Add checkbox to existing group
  const addCheckboxToGroup = useCallback((groupId) => {
    const groupFields = fields.filter(f => f.group_id === groupId);
    if (groupFields.length === 0) return;
    
    const lastField = groupFields[groupFields.length - 1];
    const newField = {
      id: `field_${Date.now()}`,
      type: 'checkbox',
      label: `${lastField.label.split(' ')[0]} Option ${groupFields.length + 1}`,
      page: lastField.page,
      x: lastField.x,
      y: lastField.y + 5,
      width: lastField.width,
      height: lastField.height,
      required: lastField.required,
      signer_index: lastField.signer_index,
      placeholder: '',
      default_value: '',
      group_id: groupId,
      group_validation: lastField.group_validation,
      checkbox_label: `Option ${groupFields.length + 1}`,
    };
    
    setCheckboxGroups(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        fields: [...prev[groupId].fields, newField.id]
      }
    }));
    
    setFields([...fields, newField]);
    setSelectedField(newField.id);
  }, [fields]);

  // Group the currently multi-selected checkboxes into one rule group.
  const groupSelectedFields = useCallback(() => {
    const chosen = fields.filter(f => selectedFields.includes(f.id) && f.type === 'checkbox');
    if (chosen.length < 2) {
      toast.error('Select at least 2 checkboxes to group');
      return;
    }
    const gid = `group_${Date.now()}`;
    const ids = chosen.map(c => c.id);
    setFields(prev => prev.map(f => ids.includes(f.id)
      ? {
          ...f,
          group_id: gid,
          group_required: true,
          group_validation: 'select_at_least',
          group_min: 1,
          group_label: 'Checkbox group',
          checkbox_label: f.checkbox_label || f.label,
          required: false,
        }
      : f));
    setCheckboxGroups(prev => ({ ...prev, [gid]: { validation: 'select_at_least', fields: ids } }));
    toast.success(`Grouped ${chosen.length} checkboxes`);
    setSelectedField(ids[0]);
    setSelectedFields([]);
  }, [fields, selectedFields]);

  // Update a property on every field in a group.
  const updateGroup = useCallback((groupId, updates) => {
    setFields(prev => prev.map(f => f.group_id === groupId ? { ...f, ...updates } : f));
    if (updates.group_validation) {
      setCheckboxGroups(prev => ({
        ...prev,
        [groupId]: { ...prev[groupId], validation: updates.group_validation },
      }));
    }
  }, []);

  // Remove all fields from their group (keep the checkboxes themselves).
  const ungroupFields = useCallback((groupId) => {
    setFields(prev => prev.map(f => f.group_id === groupId
      ? { ...f, group_id: null, group_validation: null, group_required: null, group_min: null, group_label: null }
      : f));
    setCheckboxGroups(prev => { const u = { ...prev }; delete u[groupId]; return u; });
    toast.success('Ungrouped checkboxes');
  }, []);

  // Update a field
  const updateField = useCallback((fieldId, updates) => {
    setFields(prev => prev.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  }, []);

  // Update multiple selected fields
  const updateSelectedFields = useCallback((updates) => {
    setFields(prev => prev.map(f => 
      selectedFields.includes(f.id) ? { ...f, ...updates } : f
    ));
    toast.success(`Updated ${selectedFields.length} field(s)`);
  }, [selectedFields]);

  // Delete a field
  const deleteField = useCallback((fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    
    // If part of a group, remove from group
    if (field?.group_id && checkboxGroups[field.group_id]) {
      setCheckboxGroups(prev => {
        const updated = { ...prev };
        updated[field.group_id] = {
          ...updated[field.group_id],
          fields: updated[field.group_id].fields.filter(id => id !== fieldId)
        };
        // Remove group if empty
        if (updated[field.group_id].fields.length === 0) {
          delete updated[field.group_id];
        }
        return updated;
      });
    }
    
    setFields(prev => prev.filter(f => f.id !== fieldId));
    if (selectedField === fieldId) {
      setSelectedField(null);
    }
    setSelectedFields(prev => prev.filter(id => id !== fieldId));
  }, [fields, selectedField, checkboxGroups]);

  // Toggle field selection (for multi-select)
  const toggleFieldSelection = useCallback((fieldId, event) => {
    if (event?.ctrlKey || event?.metaKey || showMultiSelectMode) {
      setSelectedFields(prev => 
        prev.includes(fieldId) 
          ? prev.filter(id => id !== fieldId)
          : [...prev, fieldId]
      );
    } else {
      setSelectedField(fieldId);
      setSelectedFields([fieldId]);
      // On phones, opening a field should reveal its properties immediately.
      if (window.matchMedia('(max-width: 767px)').matches) {
        setMobileSheet('properties');
      }
    }
  }, [showMultiSelectMode]);

  // Select all fields on current page
  const selectAllOnPage = useCallback(() => {
    const pageFields = fields.filter(f => f.page === currentPage).map(f => f.id);
    setSelectedFields(pageFields);
  }, [fields, currentPage]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedField(null);
    setSelectedFields([]);
  }, []);

  // --- Pointer-based field dragging --------------------------------------
  // Replaces the old HTML5 native drag, which fired sporadic (and sometimes
  // bogus 0,0) events so the box lagged / didn't track the cursor. Pointer
  // events + setPointerCapture give pixel-accurate, real-time tracking.
  const dragStateRef = useRef(null);

  const handleFieldPointerDown = useCallback((e, field) => {
    if (e.button !== undefined && e.button !== 0) return; // left button only
    e.stopPropagation();
    const container = pageRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cursorX = ((e.clientX - rect.left) / rect.width) * 100;
    const cursorY = ((e.clientY - rect.top) / rect.height) * 100;
    dragStateRef.current = {
      fieldId: field.id,
      offsetX: cursorX - field.x,
      offsetY: cursorY - field.y,
      startX: cursorX,
      startY: cursorY,
      moved: false,
      ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  }, []);

  const handleFieldPointerMove = useCallback((e) => {
    const st = dragStateRef.current;
    if (!st) return;
    const container = pageRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cursorX = ((e.clientX - rect.left) / rect.width) * 100;
    const cursorY = ((e.clientY - rect.top) / rect.height) * 100;
    // Ignore micro-jitter so a plain click still registers as selection.
    if (!st.moved && Math.hypot(cursorX - st.startX, cursorY - st.startY) < 0.4) return;
    st.moved = true;
    updateField(st.fieldId, {
      x: Math.max(0, Math.min(97, cursorX - st.offsetX)),
      y: Math.max(0, Math.min(98, cursorY - st.offsetY)),
    });
  }, [updateField]);

  const handleFieldPointerUp = useCallback((e, field) => {
    const st = dragStateRef.current;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    dragStateRef.current = null;
    // No real movement → treat as a click and (de)select the field.
    if (st && !st.moved) {
      toggleFieldSelection(field.id, { ctrlKey: st.ctrlKey, metaKey: st.metaKey, shiftKey: st.shiftKey });
    }
  }, [toggleFieldSelection]);

  // Stamp mode: click anywhere on the page (not on an existing field) to drop
  // a new field of the active stamp type — lets you place many in a row.
  const handlePagePointerDown = useCallback((e) => {
    if (!stampMode || !stampType) return;
    const container = pageRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;
    addFieldAt(stampType, x, y);
  }, [stampMode, stampType, addFieldAt]);

  // --- Resize handles -----------------------------------------------------
  // Drag any of the 8 handles on a selected field to resize it. On release we
  // remember the size for that field type so future Stamp/Add reuse it.
  const handleResizePointerDown = useCallback((e, field, dir) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    const container = pageRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    resizeStateRef.current = {
      fieldId: field.id,
      type: field.type,
      dir,
      startCX: ((e.clientX - rect.left) / rect.width) * 100,
      startCY: ((e.clientY - rect.top) / rect.height) * 100,
      x: field.x, y: field.y, w: field.width, h: field.height,
      finalW: field.width, finalH: field.height,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  }, []);

  const handleResizePointerMove = useCallback((e) => {
    const st = resizeStateRef.current;
    if (!st) return;
    const container = pageRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const dx = ((e.clientX - rect.left) / rect.width) * 100 - st.startCX;
    const dy = ((e.clientY - rect.top) / rect.height) * 100 - st.startCY;
    const MINW = 1.5, MINH = 1.2;
    let { x, y, w, h } = st;
    if (st.dir.r) w = st.w + dx;
    if (st.dir.l) { w = st.w - dx; x = st.x + dx; }
    if (st.dir.b) h = st.h + dy;
    if (st.dir.t) { h = st.h - dy; y = st.y + dy; }
    if (w < MINW) { if (st.dir.l) x = st.x + (st.w - MINW); w = MINW; }
    if (h < MINH) { if (st.dir.t) y = st.y + (st.h - MINH); h = MINH; }
    x = Math.max(0, x); y = Math.max(0, y);
    if (x + w > 100) w = 100 - x;
    if (y + h > 100) h = 100 - y;
    w = +w.toFixed(2); h = +h.toFixed(2);
    st.finalW = w; st.finalH = h;
    updateField(st.fieldId, { x: +x.toFixed(2), y: +y.toFixed(2), width: w, height: h });
  }, [updateField]);

  const handleResizePointerUp = useCallback((e) => {
    const st = resizeStateRef.current;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    resizeStateRef.current = null;
    if (st) {
      // Remember this size for the field type → next stamp/add reuses it.
      lastSizeByTypeRef.current[st.type] = { width: st.finalW, height: st.finalH };
    }
  }, []);

  // The 8 resize handles (corners + edge midpoints) with cursors.
  const RESIZE_HANDLES = [
    { dir: { t: 1, l: 1 }, pos: '-top-1 -left-1', cur: 'nwse-resize' },
    { dir: { t: 1 }, pos: '-top-1 left-1/2 -translate-x-1/2', cur: 'ns-resize' },
    { dir: { t: 1, r: 1 }, pos: '-top-1 -right-1', cur: 'nesw-resize' },
    { dir: { r: 1 }, pos: 'top-1/2 -right-1 -translate-y-1/2', cur: 'ew-resize' },
    { dir: { b: 1, r: 1 }, pos: '-bottom-1 -right-1', cur: 'nwse-resize' },
    { dir: { b: 1 }, pos: '-bottom-1 left-1/2 -translate-x-1/2', cur: 'ns-resize' },
    { dir: { b: 1, l: 1 }, pos: '-bottom-1 -left-1', cur: 'nesw-resize' },
    { dir: { l: 1 }, pos: 'top-1/2 -left-1 -translate-y-1/2', cur: 'ew-resize' },
  ];

  // Save template
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/esignature/templates/${templateId}/fields`,
        { 
          fields, 
          signer_roles: signerRoles,
          signing_order_type: signingOrderType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Template saved successfully');
      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving template:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Add a signer role
  const addSignerRole = () => {
    const newRole = `Signer ${signerRoles.length + 1}`;
    setSignerRoles([...signerRoles, newRole]);
  };

  // Update signer role name
  const updateSignerRole = (index, name) => {
    setSignerRoles(prev => prev.map((role, i) => i === index ? name : role));
  };

  // Remove signer role
  const removeSignerRole = (index) => {
    if (signerRoles.length <= 1) {
      toast.error('Must have at least one signer');
      return;
    }
    // Reassign any fields with this signer to first signer
    setFields(prev => prev.map(f => 
      f.signer_index === index ? { ...f, signer_index: 0 } 
        : f.signer_index > index ? { ...f, signer_index: f.signer_index - 1 }
        : f
    ));
    setSignerRoles(prev => prev.filter((_, i) => i !== index));
    if (activeSignerIndex >= index) {
      setActiveSignerIndex(Math.max(0, activeSignerIndex - 1));
    }
  };

  // Reorder signer roles (drag & drop). Remaps every field's signer_index so
  // placed fields stay attached to their original role after reordering.
  const reorderSignerRoles = (from, to) => {
    if (from === to || from == null || to == null) return;
    const n = signerRoles.length;
    if (from < 0 || to < 0 || from >= n || to >= n) return;

    const idxOrder = [...Array(n).keys()];
    const [moved] = idxOrder.splice(from, 1);
    idxOrder.splice(to, 0, moved);
    const map = {};
    idxOrder.forEach((oldIdx, newPos) => { map[oldIdx] = newPos; });

    setSignerRoles(prev => {
      const arr = [...prev];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
    setFields(prev => prev.map(f => ({ ...f, signer_index: map[f.signer_index] ?? f.signer_index })));
    setActiveSignerIndex(map[activeSignerIndex] ?? activeSignerIndex);
    toast.success('Signing order updated');
  };

  // Get fields for current page
  const currentPageFields = fields.filter(f => f.page === currentPage);
  const selectedFieldData = fields.find(f => f.id === selectedField);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 p-3 md:p-4 border-b bg-white">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} className="shrink-0 px-2 md:px-3">
            <ChevronLeft className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Back</span>
          </Button>
          <div className="min-w-0">
            <h2 className="font-semibold truncate">{template?.name || 'Template Editor'}</h2>
            <p className="text-xs md:text-sm text-muted-foreground truncate">
              {fields.length} field(s) • {signerRoles.length} signer(s)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant={showMultiSelectMode ? "default" : "outline"}
            size="sm"
            className="hidden md:inline-flex"
            onClick={() => setShowMultiSelectMode(!showMultiSelectMode)}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Multi-Select
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="hidden md:inline-flex"
            onClick={() => setShowPageMgmtModal(true)}
            data-testid="open-page-mgmt-btn"
          >
            <FileUp className="w-4 h-4 mr-2" />
            Pages
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 md:mr-2 animate-spin" /> : <Save className="w-4 h-4 md:mr-2" />}
            <span className="hidden md:inline">Save Template</span>
            <span className="md:hidden">Save</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar (desktop) / Tools bottom-sheet (mobile) */}
        <div
          className={`bg-gray-50 overflow-y-auto md:static md:z-auto md:w-72 md:max-h-none md:translate-y-0 md:rounded-none md:shadow-none md:border-r md:transition-none fixed inset-x-0 bottom-0 z-50 w-full max-h-[80vh] rounded-t-2xl shadow-2xl transition-transform duration-300 ${mobileSheet === 'tools' ? 'translate-y-0' : 'translate-y-full'}`}
          data-testid="tools-panel"
        >
          {/* Mobile sheet header */}
          <div className="md:hidden sticky top-0 z-10 flex items-center justify-between bg-white border-b px-4 py-3">
            <span className="font-medium flex items-center gap-2"><SlidersHorizontal size={16} /> Signers & Fields</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setMobileSheet(null)} data-testid="close-tools-sheet"><X size={16} /></Button>
          </div>
          {/* Active Signer Selector */}
          <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium flex items-center gap-2">
                <Users size={16} />
                Active Signer
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              New fields will be assigned to the selected signer
            </p>
            <div className="space-y-1">
              {signerRoles.map((role, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                    activeSignerIndex === index 
                      ? 'bg-primary/10 ring-2 ring-primary' 
                      : 'bg-white hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveSignerIndex(index)}
                >
                  <div className={`w-4 h-4 rounded ${getSignerColor(index)}`} />
                  <span className="flex-1 text-sm font-medium">{role}</span>
                  <Badge variant="secondary" className="text-xs">
                    {fields.filter(f => f.signer_index === index).length}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Signing Order */}
          <div className="p-4 border-b">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <ListOrdered size={16} />
              Signing Order
            </h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="signingOrder"
                  checked={signingOrderType === 'parallel'}
                  onChange={() => setSigningOrderType('parallel')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Parallel (all at once)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="signingOrder"
                  checked={signingOrderType === 'sequential'}
                  onChange={() => setSigningOrderType('sequential')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Sequential (in order)</span>
              </label>
            </div>
            {signingOrderType === 'sequential' && (
              <p className="text-xs text-muted-foreground mt-2">
                Signers must sign in the order listed above
              </p>
            )}
          </div>

          {/* Signer Roles Management */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Signer Roles</h3>
              <Button variant="ghost" size="sm" onClick={addSignerRole}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {signerRoles.length > 1 && (
              <p className="text-xs text-muted-foreground mb-2">Drag <GripVertical className="w-3 h-3 inline" /> (or use the arrows) to change signing order. Placed fields move with their role.</p>
            )}
            <div className="space-y-2">
              {signerRoles.map((role, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 rounded-md ${dragRoleIndex === index ? 'opacity-50 bg-muted' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { reorderSignerRoles(dragRoleIndex, index); setDragRoleIndex(null); }}
                  data-testid={`signer-role-row-${index}`}
                >
                  <span
                    className="cursor-grab active:cursor-grabbing shrink-0 flex items-center"
                    draggable
                    onDragStart={(e) => { setDragRoleIndex(index); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragEnd={() => setDragRoleIndex(null)}
                    data-testid={`signer-role-drag-${index}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground pointer-events-none" />
                  </span>
                  <div className={`w-3 h-3 rounded ${getSignerColor(index)}`} />
                  <span className="text-xs text-muted-foreground w-4">{index + 1}.</span>
                  <Input
                    value={role}
                    onChange={(e) => updateSignerRole(index, e.target.value)}
                    className="h-8 text-sm flex-1"
                  />
                  {signerRoles.length > 1 && (
                    <>
                      <Button variant="ghost" size="sm" className="h-8 w-6 p-0" disabled={index === 0} onClick={() => reorderSignerRoles(index, index - 1)} data-testid={`signer-role-up-${index}`}>
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-6 p-0" disabled={index === signerRoles.length - 1} onClick={() => reorderSignerRoles(index, index + 1)} data-testid={`signer-role-down-${index}`}>
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => removeSignerRole(index)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add Fields */}
          <div className="p-4 border-b">
            <h3 className="font-medium mb-3">Add Fields</h3>
            <p className="text-xs text-muted-foreground mb-2">
              Adding for: <span className={`font-medium ${getSignerColor(activeSignerIndex).replace('bg-', 'text-')}`}>
                {signerRoles[activeSignerIndex]}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {FIELD_TYPES.map(({ type, label, icon: Icon, color }) => {
                const isActiveStamp = stampMode && stampType === type;
                return (
                  <Button
                    key={type}
                    variant="outline"
                    size="sm"
                    className={`flex flex-col items-center gap-1 h-auto py-2 ${isActiveStamp ? 'ring-2 ring-amber-500 bg-amber-50' : ''}`}
                    onClick={() => {
                      if (stampMode) { setStampType(type); }
                      else {
                        addField(type);
                        if (window.matchMedia('(max-width: 767px)').matches) setMobileSheet('properties');
                      }
                    }}
                    title={stampMode ? `Stamp ${label} — then click the document to place` : `Add a ${label} field`}
                    data-testid={`add-field-${type}`}
                  >
                    <div className={`w-6 h-6 rounded flex items-center justify-center ${color} text-white`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <span className="text-xs">{label}</span>
                  </Button>
                );
              })}
            </div>

            <div className="mt-3 pt-3 border-t space-y-2">
              <Button
                variant={stampMode ? 'default' : 'outline'}
                size="sm"
                className="w-full justify-start gap-2"
                onClick={() => setStampMode(m => { const next = !m; if (!next) setStampType(null); return next; })}
                data-testid="stamp-mode-toggle"
              >
                <Stamp size={14} />
                <span className="text-xs">
                  {stampMode ? 'Stamp ON — pick a field, click to place' : 'Stamp mode (place many)'}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={() => setShowBulkCheckboxModal(true)}
                data-testid="bulk-checkbox-btn"
              >
                <Grid3x3 size={14} />
                <span className="text-xs">Bulk add fields…</span>
              </Button>
            </div>
          </div>

          {/* Multi-select Actions */}
          {selectedFields.length > 1 && (
            <div className="p-4 border-b bg-blue-50">
              <h3 className="font-medium mb-2">{selectedFields.length} Fields Selected</h3>
              <div className="space-y-2">
                <Label className="text-xs">Change Signer for All:</Label>
                <select
                  className="w-full h-8 text-sm border rounded"
                  onChange={(e) => {
                    if (e.target.value === '') return;
                    const idx = parseInt(e.target.value);
                    updateSelectedFields({ signer_index: idx });
                    setActiveSignerIndex(idx);
                  }}
                >
                  <option value="">Select signer...</option>
                  {signerRoles.map((role, idx) => (
                    <option key={idx} value={idx}>{role}</option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={groupSelectedFields}
                  data-testid="group-selected-btn"
                >
                  <ListOrdered size={14} className="mr-2" />
                  Group selected checkboxes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={clearSelection}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Fields List */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Fields ({fields.length})</h3>
              {fields.length > 0 && (
                <Button variant="ghost" size="sm" onClick={selectAllOnPage}>
                  Select All
                </Button>
              )}
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {fields.map((field) => {
                const fieldType = FIELD_TYPES.find(t => t.type === field.type || (t.type === 'checkbox_group' && field.type === 'checkbox' && field.group_id));
                const Icon = fieldType?.icon || Type;
                const isSelected = selectedFields.includes(field.id);
                const isGrouped = field.group_id;
                return (
                  <div
                    key={field.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary/10 border border-primary' : 'bg-white border hover:bg-gray-50'
                    } ${isGrouped ? 'ml-4 border-l-2 border-l-orange-500' : ''}`}
                    onClick={(e) => toggleFieldSelection(field.id, e)}
                  >
                    <div className={`w-6 h-6 rounded flex items-center justify-center ${getSignerColor(field.signer_index)} text-white`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{field.label}</p>
                      <p className="text-xs text-muted-foreground">
                        Page {field.page} • {signerRoles[field.signer_index] || 'Unknown'}
                        {isGrouped && ' • Grouped'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1 h-6 w-6 opacity-50 hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); deleteField(field.id); }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Center - PDF Preview */}
        <div className="flex-1 overflow-auto bg-gray-200 p-4 pb-28 md:pb-4" ref={pdfContainerRef}>
          <div className="flex flex-col items-center">
            {/* Page Navigation */}
            <div className="flex items-center gap-4 mb-4 bg-white rounded-lg shadow px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">
                Page {currentPage} of {numPages || 1}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage >= (numPages || 1)}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              {/* Reload PDF button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={loadPdf}
                disabled={pdfLoading}
                title="Reload PDF"
              >
                {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              </Button>
            </div>

            {/* Stamp mode banner */}
            {stampMode && stampType && (
              <div className="mb-2 flex items-center gap-2 bg-amber-100 border border-amber-300 text-amber-900 text-sm rounded-md px-3 py-1.5" data-testid="stamp-mode-banner">
                <Stamp size={14} />
                <span>Stamp mode: click the document to place a <strong>{FIELD_TYPES.find(t => t.type === stampType)?.label || stampType}</strong>.</span>
                <button className="ml-auto underline font-medium" onClick={() => { setStampMode(false); setStampType(null); }} data-testid="stamp-mode-exit">Done</button>
              </div>
            )}

            {/* PDF with Field Overlays */}
            <div 
              className={`relative bg-white shadow-lg ${stampMode && stampType ? 'cursor-crosshair' : ''}`}
              ref={pageRef}
              style={{ width: `${renderWidth}px`, minHeight: `${Math.round(renderWidth * 792 / 612)}px` }}
              onPointerDown={handlePagePointerDown}
            >
              {pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Loading PDF...</p>
                  </div>
                </div>
              )}
              
              {pdfError && !pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                  <div className="text-center p-6">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-4">Failed to load PDF preview</p>
                    <Button onClick={loadPdf}>
                      <Loader2 className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                </div>
              )}
              
              {pdfUrl && !pdfError && (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => {
                    console.error('PDF load error:', error);
                    setPdfError(true);
                  }}
                  loading={
                    <div className="flex items-center justify-center h-[792px]">
                      <Loader2 className="w-8 h-8 animate-spin" />
                    </div>
                  }
                  error={
                    <div className="flex flex-col items-center justify-center h-[792px] text-muted-foreground">
                      <p className="mb-4">Failed to render PDF</p>
                      <Button onClick={loadPdf} variant="outline" size="sm">
                        Retry
                      </Button>
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPage}
                    width={renderWidth}
                    onLoadSuccess={onPageLoadSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              )}

              {/* Field Overlays */}
              {currentPageFields.map((field) => {
                const fieldType = FIELD_TYPES.find(t => t.type === field.type);
                const Icon = fieldType?.icon || Type;
                const isSelected = selectedFields.includes(field.id);
                
                return (
                  <div
                    key={field.id}
                    className={`absolute transition-all ${stampMode ? 'cursor-crosshair' : 'cursor-move'} ${
                      isSelected ? 'ring-2 ring-primary z-10' : ''
                    }`}
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`,
                      touchAction: 'none',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => handleFieldPointerDown(e, field)}
                    onPointerMove={handleFieldPointerMove}
                    onPointerUp={(e) => handleFieldPointerUp(e, field)}
                  >
                    <div className={`w-full h-full ${getSignerColor(field.signer_index)} bg-opacity-30 border-2 border-dashed rounded flex items-center justify-center`}
                         style={{ borderColor: isSelected ? 'var(--primary)' : 'currentColor' }}>
                      <div className={`${getSignerColor(field.signer_index)} text-white px-1 py-0.5 rounded text-xs flex items-center gap-1`}>
                        <Icon className="w-3 h-3" />
                        <span className="truncate max-w-[80px]">{field.checkbox_label || field.label}</span>
                      </div>
                    </div>
                    {isSelected && RESIZE_HANDLES.map((hdl, hi) => (
                      <div
                        key={hi}
                        className={`absolute w-2.5 h-2.5 bg-white border-2 border-primary rounded-sm z-20 ${hdl.pos} after:content-[''] after:absolute after:-inset-3 md:after:-inset-1`}
                        style={{ cursor: hdl.cur, touchAction: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => handleResizePointerDown(e, field, hdl.dir)}
                        onPointerMove={handleResizePointerMove}
                        onPointerUp={handleResizePointerUp}
                        data-testid={`resize-handle-${hi}`}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Sidebar (desktop) / Properties bottom-sheet (mobile) */}
        <div
          className={`bg-gray-50 overflow-y-auto md:static md:z-auto md:w-72 md:max-h-none md:translate-y-0 md:rounded-none md:shadow-none md:border-l md:transition-none fixed inset-x-0 bottom-0 z-50 w-full max-h-[80vh] rounded-t-2xl shadow-2xl transition-transform duration-300 ${mobileSheet === 'properties' ? 'translate-y-0' : 'translate-y-full'}`}
          data-testid="properties-panel"
        >
          {/* Mobile sheet header */}
          <div className="md:hidden sticky top-0 z-10 flex items-center justify-between bg-white border-b px-4 py-3">
            <span className="font-medium flex items-center gap-2"><SlidersHorizontal size={16} /> Field Properties</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setMobileSheet(null)} data-testid="close-properties-sheet"><X size={16} /></Button>
          </div>
          {selectedFieldData ? (
            <div className="p-4 space-y-4">
              <h3 className="font-medium">Field Properties</h3>
              
              <div>
                <Label className="text-sm">Label</Label>
                <Input
                  value={selectedFieldData.label}
                  onChange={(e) => updateField(selectedField, { label: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm">Assigned To</Label>
                <select
                  className="w-full h-9 mt-1 border rounded-md px-3"
                  value={selectedFieldData.signer_index}
                  onChange={(e) => {
                    const idx = parseInt(e.target.value);
                    updateField(selectedField, { signer_index: idx });
                    // Make this the active signer so new fields stay with the
                    // same person until you pick a different one.
                    setActiveSignerIndex(idx);
                  }}
                >
                  {signerRoles.map((role, idx) => (
                    <option key={idx} value={idx}>{role}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={selectedFieldData.required}
                  onChange={(e) => updateField(selectedField, { required: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="required" className="text-sm">Required</Label>
              </div>

              {/* Default value — auto-fills every new signing request created from
                  this template. Signers can still overwrite at signing time. */}
              {['text', 'date', 'name', 'email'].includes(selectedFieldData.type) && (
                <div>
                  <Label className="text-sm">Pre-fill default</Label>
                  <Input
                    value={selectedFieldData.default_value || ''}
                    onChange={(e) => updateField(selectedField, { default_value: e.target.value })}
                    className="mt-1"
                    placeholder="Leave blank if signer fills it"
                    data-testid="field-default-value"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Pre-filled on every new request — signer can still change it.
                  </p>
                </div>
              )}
              {selectedFieldData.type === 'checkbox' && !selectedFieldData.group_id && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="default-checked"
                    checked={!!selectedFieldData.default_value}
                    onChange={(e) => updateField(selectedField, { default_value: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="default-checked" className="text-sm">Default checked</Label>
                </div>
              )}

              {/* Checkbox Group Properties */}
              {selectedFieldData.group_id && (() => {
                const gid = selectedFieldData.group_id;
                const groupSize = fields.filter(f => f.group_id === gid).length;
                const isRequired = selectedFieldData.group_required != null
                  ? selectedFieldData.group_required
                  : (selectedFieldData.group_validation && selectedFieldData.group_validation !== 'optional');
                const rule = selectedFieldData.group_validation === 'select_at_least_one'
                  ? 'select_at_least'
                  : (selectedFieldData.group_validation || 'select_at_least');
                const minN = selectedFieldData.group_min || 1;
                return (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <ListOrdered size={14} />
                    Checkbox Group ({groupSize})
                  </h4>

                  <div className="mb-3">
                    <Label className="text-sm">Group name</Label>
                    <Input
                      value={selectedFieldData.group_label || ''}
                      onChange={(e) => updateGroup(gid, { group_label: e.target.value })}
                      className="mt-1"
                      placeholder="e.g. Acknowledgements"
                      data-testid="group-label-input"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="text-sm">Option Label (this checkbox)</Label>
                    <Input
                      value={selectedFieldData.checkbox_label || ''}
                      onChange={(e) => updateField(selectedField, { checkbox_label: e.target.value })}
                      className="mt-1"
                      placeholder="Option text"
                    />
                  </div>

                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!isRequired}
                      onChange={(e) => updateGroup(gid, e.target.checked
                        ? { group_required: true, group_validation: rule, group_min: minN }
                        : { group_required: false })}
                      className="w-4 h-4"
                      data-testid="group-required-toggle"
                    />
                    <span className="text-sm font-medium">Group is required</span>
                  </label>

                  {isRequired && (
                    <>
                      <div className="mb-3">
                        <Label className="text-sm">Rule</Label>
                        <select
                          className="w-full h-9 mt-1 border rounded-md px-3 text-sm"
                          value={rule}
                          onChange={(e) => updateGroup(gid, { group_validation: e.target.value })}
                          data-testid="group-rule-select"
                        >
                          {CHECKBOX_GROUP_VALIDATIONS.map(v => (
                            <option key={v.value} value={v.value}>{v.label}</option>
                          ))}
                        </select>
                      </div>
                      {rule === 'select_at_least' && (
                        <div className="mb-3">
                          <Label className="text-sm">Minimum required (of {groupSize})</Label>
                          <Input
                            type="number"
                            min={1}
                            max={groupSize}
                            value={minN}
                            onChange={(e) => updateGroup(gid, { group_min: Math.max(1, Math.min(groupSize, parseInt(e.target.value, 10) || 1)) })}
                            className="mt-1"
                            data-testid="group-min-input"
                          />
                        </div>
                      )}
                    </>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mb-2"
                    onClick={() => addCheckboxToGroup(gid)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Option to Group
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-red-600"
                    onClick={() => ungroupFields(gid)}
                    data-testid="ungroup-btn"
                  >
                    Ungroup
                  </Button>

                  <p className="text-xs text-muted-foreground mt-2">
                    {isRequired
                      ? (rule === 'select_one' ? 'Signer must select exactly one.'
                        : rule === 'select_all' ? `Signer must select all ${groupSize}.`
                        : `Signer must select at least ${minN} of ${groupSize}.`)
                      : 'Optional — signer may skip this group.'}
                  </p>
                </div>
                );
              })()}

              {/* Position/Size */}
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-2">Position & Size</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">X (%)</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedFieldData.x)}
                      onChange={(e) => updateField(selectedField, { x: parseFloat(e.target.value) })}
                      className="mt-1 h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Y (%)</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedFieldData.y)}
                      onChange={(e) => updateField(selectedField, { y: parseFloat(e.target.value) })}
                      className="mt-1 h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Width (%)</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedFieldData.width)}
                      onChange={(e) => { const v = parseFloat(e.target.value); updateField(selectedField, { width: v }); if (!isNaN(v) && selectedFieldData) lastSizeByTypeRef.current[selectedFieldData.type] = { width: v, height: selectedFieldData.height }; }}
                      className="mt-1 h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Height (%)</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedFieldData.height)}
                      onChange={(e) => { const v = parseFloat(e.target.value); updateField(selectedField, { height: v }); if (!isNaN(v) && selectedFieldData) lastSizeByTypeRef.current[selectedFieldData.type] = { width: selectedFieldData.width, height: v }; }}
                      className="mt-1 h-8"
                    />
                  </div>
                </div>
              </div>

              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={() => deleteField(selectedField)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Field
              </Button>
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <p>Select a field to edit its properties</p>
              <p className="text-xs mt-2">
                Tip: Hold Ctrl/Cmd and click to select multiple fields
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile backdrop for bottom sheets */}
      {mobileSheet && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileSheet(null)}
          data-testid="mobile-sheet-backdrop"
        />
      )}

      {/* Mobile bottom action bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t px-3 py-2 flex items-center justify-between gap-2" data-testid="mobile-bottom-bar">
        <button
          className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-gray-300 bg-white"
          onClick={() => setMobileSheet('tools')}
          data-testid="mobile-active-signer-pill"
        >
          <span className={`w-6 h-6 rounded-full ${getSignerColor(activeSignerIndex)} text-white flex items-center justify-center`}>
            <Users size={13} />
          </span>
          <span className="text-xs font-medium max-w-[80px] truncate">{signerRoles[activeSignerIndex] || 'Signer'}</span>
        </button>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-9 w-9 p-0" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="mobile-page-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs tabular-nums w-12 text-center">{currentPage}/{numPages || 1}</span>
          <Button variant="ghost" size="sm" className="h-9 w-9 p-0" disabled={currentPage >= (numPages || 1)} onClick={() => setCurrentPage(p => p + 1)} data-testid="mobile-page-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => setShowPageMgmtModal(true)} data-testid="mobile-pages-btn">
            <FileUp className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            className="h-9 rounded-full shadow-md"
            onClick={() => setMobileSheet('tools')}
            data-testid="mobile-add-field-fab"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Bulk Add Fields Modal */}
      {showBulkCheckboxModal && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="bulk-checkbox-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-base font-semibold inline-flex items-center gap-2">
                <Grid3x3 size={16} className="text-primary" />
                Bulk add fields
              </h3>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShowBulkCheckboxModal(false)}>
                <X size={16} />
              </Button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Generates the requested number of fields in a grid on this page.
                You can drag any individual field afterwards.
              </p>
              <div>
                <Label className="text-sm">Field type</Label>
                <select
                  className="w-full h-9 mt-1 border rounded-md px-3 text-sm"
                  value={bulkType}
                  onChange={e => setBulkType(e.target.value)}
                  data-testid="bulk-field-type"
                >
                  {FIELD_TYPES.filter(t => t.type !== 'checkbox_group').map(t => (
                    <option key={t.type} value={t.type}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm">How many?</Label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={bulkCount}
                  onChange={e => setBulkCount(parseInt(e.target.value, 10) || 1)}
                  className="mt-1"
                  data-testid="bulk-checkbox-count"
                />
              </div>
              <div>
                <Label className="text-sm">Columns per row</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={bulkColumns}
                  onChange={e => setBulkColumns(parseInt(e.target.value, 10) || 1)}
                  className="mt-1"
                  data-testid="bulk-checkbox-cols"
                />
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Adds for signer: <strong>{signerRoles[activeSignerIndex] || 'Signer'}</strong>. Switch signer above first if needed.
              </p>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowBulkCheckboxModal(false)}>Cancel</Button>
              <Button
                size="sm"
                onClick={() => {
                  addFieldGrid(bulkType, bulkCount, bulkColumns);
                  setShowBulkCheckboxModal(false);
                }}
                data-testid="bulk-checkbox-confirm"
              >
                Add {bulkCount} {(FIELD_TYPES.find(t => t.type === bulkType)?.label || 'field').toLowerCase()}{bulkCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Page Management Modal */}
      {showPageMgmtModal && (
        <PageManagementModal
          templateId={templateId}
          numPages={numPages}
          inProgress={pageOpInProgress}
          setInProgress={setPageOpInProgress}
          onClose={() => setShowPageMgmtModal(false)}
          onChanged={() => {
            setShowPageMgmtModal(false);
            // Reload the template so the editor picks up the new PDF + page count
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * PageManagementModal — Phase 3: append/insert/replace pages in the source PDF
 * without re-creating the entire template. Field positions are preserved for
 * pages that aren't touched.
 */
function PageManagementModal({ templateId, numPages, inProgress, setInProgress, onClose, onChanged }) {
  const [op, setOp] = useState('append');  // append | insert | replace
  const [insertAt, setInsertAt] = useState(1);
  const [replaceAt, setReplaceAt] = useState(1);
  const [file, setFile] = useState(null);
  const [appendBlank, setAppendBlank] = useState(true);

  const doSubmit = async () => {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('operation', op);
    if (op === 'append') {
      if (!appendBlank && !file) {
        toast.error('Pick a PDF to append, or check "Blank page"');
        return;
      }
      fd.append('append_blank', appendBlank ? 'true' : 'false');
      if (!appendBlank && file) fd.append('file', file);
    } else if (op === 'insert') {
      if (!file) { toast.error('Pick a PDF page to insert'); return; }
      fd.append('file', file);
      fd.append('position', String(insertAt));
    } else if (op === 'replace') {
      if (!file) { toast.error('Pick a replacement page PDF'); return; }
      fd.append('file', file);
      fd.append('position', String(replaceAt));
    }
    setInProgress(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/esignature/templates/${templateId}/pages`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Done — template now has ${res.data?.page_count || '?'} pages`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    } finally {
      setInProgress(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="page-mgmt-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-base font-semibold inline-flex items-center gap-2">
            <FileUp size={16} className="text-primary" />
            Edit document pages
          </h3>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose} disabled={inProgress}>
            <X size={16} />
          </Button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'append', label: 'Add to end', icon: FilePlus },
              { id: 'insert', label: 'Insert page', icon: FileUp },
              { id: 'replace', label: 'Replace page', icon: Replace },
            ].map(o => (
              <button
                key={o.id}
                onClick={() => setOp(o.id)}
                disabled={inProgress}
                data-testid={`page-op-${o.id}`}
                className={`p-2 rounded-md border text-xs flex flex-col items-center gap-1 transition ${
                  op === o.id ? 'border-primary bg-primary/5 text-primary' : 'border-muted-foreground/20 hover:bg-muted/30'
                }`}
              >
                <o.icon size={16} />
                {o.label}
              </button>
            ))}
          </div>

          {op === 'append' && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={appendBlank} onChange={e => setAppendBlank(e.target.checked)} />
                Append a blank letter-size page (good for adding a signature page)
              </label>
              {!appendBlank && (
                <div>
                  <Label className="text-sm">Or upload a PDF to append</Label>
                  <DropZone
                    file={file}
                    onFile={(f) => setFile(f)}
                    onClear={() => setFile(null)}
                    accept="application/pdf,.pdf"
                    acceptExtensions={['pdf']}
                    label="Drop a PDF to append"
                    testId="page-op-append-upload"
                    compact
                    className="mt-1"
                  />
                </div>
              )}
            </>
          )}
          {op === 'insert' && (
            <>
              <div>
                <Label className="text-sm">Insert at position (will become this page #)</Label>
                <Input type="number" min={1} max={(numPages || 1) + 1} value={insertAt} onChange={e => setInsertAt(parseInt(e.target.value, 10) || 1)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">PDF to insert</Label>
                <DropZone
                  file={file}
                  onFile={(f) => setFile(f)}
                  onClear={() => setFile(null)}
                  accept="application/pdf,.pdf"
                  acceptExtensions={['pdf']}
                  label="Drop a PDF to insert"
                  testId="page-op-insert-upload"
                  compact
                  className="mt-1"
                />
              </div>
            </>
          )}
          {op === 'replace' && (
            <>
              <div>
                <Label className="text-sm">Replace page #</Label>
                <Input type="number" min={1} max={numPages || 1} value={replaceAt} onChange={e => setReplaceAt(parseInt(e.target.value, 10) || 1)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Replacement page PDF</Label>
                <DropZone
                  file={file}
                  onFile={(f) => setFile(f)}
                  onClear={() => setFile(null)}
                  accept="application/pdf,.pdf"
                  acceptExtensions={['pdf']}
                  label="Drop a PDF replacement"
                  testId="page-op-replace-upload"
                  compact
                  className="mt-1"
                />
              </div>
            </>
          )}
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Fields you've already placed on <strong>other</strong> pages are kept. Fields on a replaced/inserted page may need to be re-positioned.
          </p>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={inProgress}>Cancel</Button>
          <Button size="sm" onClick={doSubmit} disabled={inProgress} data-testid="page-op-confirm">
            {inProgress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {inProgress ? 'Working…' : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
